import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ocupacaoRedeService from '../services/ocupacaoRedeService';
import sinalCtoService from '../services/sinalCtoService';
import {
  applySignalFilters,
  deduplicateSignalRows,
  enrichSignalRows,
} from '../utils/sinalCtoAnalytics';
import { normalizeCtoRow } from '../utils/ocupacaoRedeAnalytics';

const SinalCtoDataContext = createContext(null);

const isAbortError = (error) => (
  error?.name === 'AbortError'
  || error?.name === 'CanceledError'
  || error?.code === 'ERR_CANCELED'
);

const isIxcUnavailable = (error) => error?.response?.data?.code === 'IXC_UNAVAILABLE';

export const SinalCtoDataProvider = ({ children }) => {
  const [sinalRowsRaw, setSinalRowsRaw] = useState([]);
  const [ocupacaoRowsRaw, setOcupacaoRowsRaw] = useState([]);
  const [qtdCabo, setQtdCabo] = useState({ quantidade_cabo: 0, por_projeto: [] });
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [sourceErrors, setSourceErrors] = useState({
    sinal: '',
    ocupacao: '',
    cabo: '',
  });

  const reload = useCallback(() => {
    setRefreshToken((current) => current + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setSourceErrors({ sinal: '', ocupacao: '', cabo: '' });

      const [sinalResult, caboResult, ocupacaoResult] = await Promise.allSettled([
        sinalCtoService.buscarSinalCtos(controller.signal),
        sinalCtoService.buscarQtdCabo(controller.signal),
        ocupacaoRedeService.buscarCtoPortas(controller.signal),
      ]);

      if (!mounted) {
        return;
      }

      const nextErrors = { sinal: '', ocupacao: '', cabo: '' };

      if (sinalResult.status === 'fulfilled') {
        const rows = Array.isArray(sinalResult.value?.data?.sinal_ctos)
          ? sinalResult.value.data.sinal_ctos
          : [];
        setSinalRowsRaw(deduplicateSignalRows(rows));
      } else if (!isAbortError(sinalResult.reason)) {
        setSinalRowsRaw([]);
        nextErrors.sinal = isIxcUnavailable(sinalResult.reason)
          ? 'O IXC da empresa selecionada está indisponível para a leitura de sinal.'
          : 'Não foi possível carregar as CTOs com sinal agora.';
      }

      if (caboResult.status === 'fulfilled') {
        setQtdCabo(caboResult.value?.data?.qtd_cabo || { quantidade_cabo: 0, por_projeto: [] });
      } else if (!isAbortError(caboResult.reason)) {
        setQtdCabo({ quantidade_cabo: 0, por_projeto: [] });
        nextErrors.cabo = isIxcUnavailable(caboResult.reason)
          ? 'O IXC da empresa selecionada está indisponível para a leitura de cabos.'
          : 'Não foi possível carregar a quantidade de cabo.';
      }

      if (ocupacaoResult.status === 'fulfilled') {
        const rows = Array.isArray(ocupacaoResult.value?.data?.cto_portas)
          ? ocupacaoResult.value.data.cto_portas
          : [];
        setOcupacaoRowsRaw(rows.map((row, index) => normalizeCtoRow(row, index)));
      } else if (!isAbortError(ocupacaoResult.reason)) {
        setOcupacaoRowsRaw([]);
        nextErrors.ocupacao = 'Não foi possível enriquecer os dados com cidade, POP e projeto.';
      }

      setSourceErrors(nextErrors);
      setLoading(false);
    };

    load();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [refreshToken]);

  const rowsEnriched = useMemo(
    () => enrichSignalRows(sinalRowsRaw, ocupacaoRowsRaw),
    [ocupacaoRowsRaw, sinalRowsRaw],
  );

  const rowsFiltered = useMemo(
    () => applySignalFilters(rowsEnriched, filters),
    [filters, rowsEnriched],
  );

  const setFieldFilter = useCallback((field, value) => {
    setFilters((current) => {
      const withoutField = current.filter((filter) => filter.field !== field);

      if (value === null || value === undefined || value === '') {
        return withoutField;
      }

      return [
        ...withoutField,
        {
          field,
          value,
        },
      ];
    });
  }, []);

  const toggleFilter = useCallback((filterToToggle) => {
    setFilters((current) => (
      current.some((filter) => filter.field === filterToToggle.field && String(filter.value) === String(filterToToggle.value))
        ? current.filter((filter) => !(filter.field === filterToToggle.field && String(filter.value) === String(filterToToggle.value)))
        : [...current, filterToToggle]
    ));
  }, []);

  const removeFilter = useCallback((filterToRemove) => {
    setFilters((current) => current.filter((filter) => !(
      filter.field === filterToRemove.field
      && String(filter.value) === String(filterToRemove.value)
    )));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters([]);
  }, []);

  const value = useMemo(() => ({
    sinalRowsRaw,
    ocupacaoRowsRaw,
    rowsEnriched,
    rowsFiltered,
    qtdCabo,
    filters,
    loading,
    sourceErrors,
    reload,
    setFieldFilter,
    toggleFilter,
    removeFilter,
    clearFilters,
  }), [
    clearFilters,
    filters,
    loading,
    ocupacaoRowsRaw,
    qtdCabo,
    reload,
    removeFilter,
    rowsEnriched,
    rowsFiltered,
    setFieldFilter,
    sinalRowsRaw,
    sourceErrors,
    toggleFilter,
  ]);

  return (
    <SinalCtoDataContext.Provider value={value}>
      {children}
    </SinalCtoDataContext.Provider>
  );
};

export const useSinalCtoData = () => {
  const context = useContext(SinalCtoDataContext);

  if (!context) {
    throw new Error('useSinalCtoData must be used within SinalCtoDataProvider');
  }

  return context;
};

export default SinalCtoDataContext;
